import React from 'react';
import {
  Card as MuiCard,
  CardContent,
  CardActions,
  CardHeader,
  CardMedia,
  Box,
  useTheme,
  SxProps,
  Theme,
} from '@mui/material';

export interface CardProps {
  children?: React.ReactNode;
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  avatar?: React.ReactNode;
  action?: React.ReactNode;
  media?: string;
  mediaHeight?: number;
  actions?: React.ReactNode;
  variant?: 'default' | 'outlined' | 'elevated';
  padding?: 'none' | 'small' | 'medium' | 'large';
  hoverable?: boolean;
  clickable?: boolean;
  onClick?: () => void;
  sx?: SxProps<Theme>;
}

export const Card: React.FC<CardProps> = ({
  children,
  title,
  subtitle,
  avatar,
  action,
  media,
  mediaHeight = 200,
  actions,
  variant = 'default',
  padding = 'medium',
  hoverable = false,
  clickable = false,
  onClick,
  sx,
}) => {
  const theme = useTheme();

  // Get padding values based on size
  const getPadding = () => {
    switch (padding) {
      case 'none':
        return 0;
      case 'small':
        return 2;
      case 'medium':
        return 3;
      case 'large':
        return 4;
      default:
        return 3;
    }
  };

  // Get card styles based on variant
  const getCardStyles = (): SxProps<Theme> => {
    const baseStyles: SxProps<Theme> = {
      borderRadius: 3,
      transition: 'all 0.2s ease-in-out',
    };

    if (clickable || onClick) {
      baseStyles.cursor = 'pointer';
    }

    if (hoverable || clickable || onClick) {
      (baseStyles as any)['&:hover'] = {
        transform: 'translateY(-2px)',
        boxShadow: theme.shadows[4],
      };
    }

    switch (variant) {
      case 'outlined':
        return {
          ...baseStyles,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: 'none',
          '&:hover': {
            ...((baseStyles as any)['&:hover']),
            borderColor: theme.palette.primary.main,
          },
        };
      case 'elevated':
        return {
          ...baseStyles,
          boxShadow: theme.shadows[4],
          '&:hover': {
            ...((baseStyles as any)['&:hover']),
            boxShadow: theme.shadows[8],
          },
        };
      default:
        return baseStyles;
    }
  };

  return (
    <MuiCard
      onClick={onClick}
      sx={[
        getCardStyles(),
        ...(Array.isArray(sx) ? sx : [sx])
      ]}
    >
      {/* Card Header */}
      {(title || subtitle || avatar || action) && (
        <CardHeader
          avatar={avatar}
          action={action}
          title={title}
          subheader={subtitle}
          sx={{
            pb: children || media ? 1 : 2,
            px: getPadding(),
            pt: getPadding(),
          }}
        />
      )}

      {/* Card Media */}
      {media && (
        <CardMedia
          component="img"
          height={mediaHeight}
          image={media}
          sx={{
            objectFit: 'cover',
          }}
        />
      )}

      {/* Card Content */}
      {children && (
        <CardContent
          sx={{
            p: getPadding(),
            pt: (title || subtitle || avatar || action) ? 1 : getPadding(),
            pb: actions ? 1 : getPadding(),
            '&:last-child': {
              pb: actions ? 1 : getPadding(),
            },
          }}
        >
          {children}
        </CardContent>
      )}

      {/* Card Actions */}
      {actions && (
        <CardActions
          sx={{
            px: getPadding(),
            pb: getPadding(),
            pt: 0,
            justifyContent: 'flex-end',
          }}
        >
          <Box sx={{ display: 'flex', gap: 1 }}>
            {actions}
          </Box>
        </CardActions>
      )}
    </MuiCard>
  );
};

export default Card;